import React, { createContext, useState, useEffect, useContext } from 'react';
import { apiFetch, getToken, setToken, removeToken } from '../utils/auth';

// Create the context
const UserContext = createContext(null);

// Create a custom hook for easy consumption
export const useUser = () => useContext(UserContext);

// Create the provider component
export const UserProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [token, setTokenState] = useState(getToken());

    const fetchCurrentUser = async (authToken) => {
        const activeToken = authToken || token;
        if (!activeToken) {
            setCurrentUser(null);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const response = await apiFetch('/users/me');

            if (response.ok) {
                const data = await response.json();
                setCurrentUser(data);
            } else {
                console.warn("Could not fetch current user details");
                setCurrentUser(null);
            }
        } catch (err) {
            setError(err.message);
            console.error("User fetch error:", err);
            setCurrentUser(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCurrentUser();
    }, [token]);

    const login = (newToken) => {
        setToken(newToken);
        setLoading(true);
        setTokenState(newToken);
    };

    const logout = () => {
        removeToken();
        setTokenState(null);
        setCurrentUser(null);
        setLoading(false);
    };

    const value = {
        currentUser,
        loading,
        error,
        login,
        logout,
        refreshUser: fetchCurrentUser
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};